import type { SubmitAnamnesisPayload, Student, Address, PhoneNumber, Anamnesis } from '../../../types';
import { resolveCallingCode } from '../../../constants/callingCodes';
import {
  maskCpf,
  maskInstagram,
  maskPhoneByCountry,
  maskPostalCode,
  maskHeight,
  maskWeight,
  formatHeightMeters,
  formatWeightKg,
  unmaskCpf,
  unmaskInstagram,
  unmaskPhone,
  unmaskPostalCode,
  unmaskHeight,
  unmaskWeight,
} from '../../../utils/masks';
import { validateEmail, validateName, validatePhone } from '../../../utils/validation';
import { validateAnamneseContentFields } from '../../../utils/anamneseValidation';
import type { PublicAnamneseFormState, PublicAnamneseLockedFields } from './publicAnamnese.types';

export const createEmptyPublicAnamneseLockedFields = (): PublicAnamneseLockedFields => ({
  studentName: false,
  email: false,
  birthDate: false,
  cpf: false,
  phone: false,
});

export const resolvePublicAnamneseLockedFields = (
  student: Student,
  phone: PhoneNumber | null,
): PublicAnamneseLockedFields => ({
  studentName: true,
  email: true,
  cpf: true,
  birthDate: Boolean(student.birth_date?.trim()),
  phone: Boolean(phone?.phone_number?.trim()),
});

export const createInitialPublicAnamneseForm = (): PublicAnamneseFormState => ({
  email: '',
  studentName: '',
  birthDate: '',
  heightCm: '',
  weightKg: '',
  cpf: '',
  cep: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
  country: 'BR',
  callingCode: '55',
  phone: '',
  profession: '',
  instagram: '',
  previousOnlineConsultancy: '',
  onlineCourse: '',
  otherCourse: '',
  courseFeedback: '',
  trainingLevel: '',
  trainingLevelOther: '',
  trainingLocation: '',
  fullGymName: '',
  gymEquipmentDescription: '',
  availableEquipment: '',
  weeklyAvailability: '',
  availableTrainingTime: '',
  hadInjury: null,
  injuryDescription: '',
  hasPain: null,
  painLocation: '',
  painLimitsTraining: null,
  limitedExercises: '',
  currentlyExercises: null,
  exerciseModalities: '',
  timeWithoutExercise: '',
  exerciseHistory: '',
  enjoysTraining: null,
  usedToEffort: null,
  hasNutritionalSupport: null,
  dietDetail: '',
  mainGoal: '',
  goalDetails: '',
  takesMedication: null,
  medications: '',
  hadSurgery: null,
  surgeries: '',
  hasChronicDisease: null,
  chronicDiseases: '',
  hasFamilyHistory: null,
  familyHistoryDescription: '',
  hasChildren: null,
  timeSinceDelivery: '',
  abdominalDiastasis: null,
  diastasisTreatment: '',
  additionalInfo: '',
  referralSource: '',
  motivation: '',
});

export const sanitizePublicAnamneseForm = (data: PublicAnamneseFormState): PublicAnamneseFormState => {
  const nextData = { ...data };

  if (nextData.onlineCourse !== 'other') {
    nextData.otherCourse = '';
  }

  if (nextData.trainingLevel !== 'other') {
    nextData.trainingLevelOther = '';
  }

  if (nextData.trainingLocation !== 'full_gym') {
    nextData.fullGymName = '';
    nextData.gymEquipmentDescription = '';
  }

  if (nextData.trainingLocation !== 'home_condo_gym') {
    nextData.availableEquipment = '';
  }

  if (nextData.hadInjury !== true) {
    nextData.injuryDescription = '';
  }

  if (nextData.hasPain !== true) {
    nextData.painLocation = '';
    nextData.painLimitsTraining = null;
    nextData.limitedExercises = '';
  } else if (nextData.painLimitsTraining !== true) {
    nextData.limitedExercises = '';
  }

  if (nextData.currentlyExercises !== true) {
    nextData.exerciseModalities = '';
  }

  if (nextData.currentlyExercises !== false) {
    nextData.timeWithoutExercise = '';
  }

  if (nextData.takesMedication !== true) {
    nextData.medications = '';
  }

  if (nextData.hadSurgery !== true) {
    nextData.surgeries = '';
  }

  if (nextData.hasChronicDisease !== true) {
    nextData.chronicDiseases = '';
  }

  if (nextData.hasFamilyHistory !== true) {
    nextData.familyHistoryDescription = '';
  }

  if (nextData.hasChildren !== true) {
    nextData.timeSinceDelivery = '';
    nextData.abdominalDiastasis = null;
    nextData.diastasisTreatment = '';
  } else if (nextData.abdominalDiastasis !== true) {
    nextData.diastasisTreatment = '';
  }

  return nextData;
};

const validateStudentIdentifier = (
  email: string,
  cpfDigits: string,
): { email?: string; cpf?: string } => {
  const errors: Record<string, string> = {};
  const trimmedEmail = email.trim();
  const emailError = validateEmail(trimmedEmail);
  if (emailError) {
    errors.email = emailError;
  }

  if (!cpfDigits) {
    errors.cpf = 'Informe o CPF.';
  } else if (cpfDigits.length !== 11) {
    errors.cpf = 'Digite um CPF válido com 11 números.';
  }

  return errors;
};

export const validatePublicAnamneseForm = (
  data: PublicAnamneseFormState,
  options: { minimalPersonalData?: boolean } = {},
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const trimmedEmail = data.email.trim();
  const cpfDigits = unmaskCpf(data.cpf);
  const { minimalPersonalData = false } = options;

  const setError = (field: string, message?: string) => {
    if (message) {
      errors[field] = message;
    }
  };

  setError('studentName', validateName(data.studentName));
  setError('email', validateEmail(trimmedEmail));

  if (minimalPersonalData) {
    Object.assign(errors, validateAnamneseContentFields(data));
  } else {
    Object.assign(errors, validateStudentIdentifier(trimmedEmail, cpfDigits));
    setError('birthDate', data.birthDate.trim() ? undefined : 'Informe a data de nascimento.');
    setError('phone', validatePhone(data.phone, data.callingCode));
    Object.assign(errors, validateAnamneseContentFields(data));
  }

  return errors;
};

export const PUBLIC_ANAMNESE_FIELD_ELEMENT_IDS: Record<string, string> = {
  email: 'public-anamnese-email',
  studentName: 'public-anamnese-student-name',
  birthDate: 'public-anamnese-birth-date',
  heightCm: 'public-anamnese-height',
  weightKg: 'public-anamnese-weight',
  cpf: 'public-anamnese-cpf',
  callingCode: 'public-anamnese-calling-code',
  phone: 'public-anamnese-phone',
  profession: 'public-anamnese-profession',
  instagram: 'public-anamnese-instagram',
  previousOnlineConsultancy: 'public-anamnese-previous-consultancy',
  onlineCourse: 'public-anamnese-online-course',
  otherCourse: 'public-anamnese-other-course',
  courseFeedback: 'public-anamnese-course-feedback',
  trainingLevel: 'public-anamnese-training-level',
  trainingLevelOther: 'public-anamnese-training-level-other',
  trainingLocation: 'public-anamnese-training-location',
  fullGymName: 'public-anamnese-full-gym-name',
  availableEquipment: 'public-anamnese-available-equipment',
  weeklyAvailability: 'public-anamnese-weekly-availability',
  availableTrainingTime: 'public-anamnese-available-training-time',
  hadInjury: 'public-anamnese-had-injury',
  injuryDescription: 'public-anamnese-injury-description',
  hasPain: 'public-anamnese-has-pain',
  painLocation: 'public-anamnese-pain-location',
  painLimitsTraining: 'public-anamnese-pain-limits-training',
  limitedExercises: 'public-anamnese-limited-exercises',
  currentlyExercises: 'public-anamnese-currently-exercises',
  exerciseModalities: 'public-anamnese-exercise-modalities',
  timeWithoutExercise: 'public-anamnese-time-without-exercise',
  exerciseHistory: 'public-anamnese-exercise-history',
  enjoysTraining: 'public-anamnese-enjoys-training',
  usedToEffort: 'public-anamnese-used-to-effort',
  hasNutritionalSupport: 'public-anamnese-has-nutritional-support',
  dietDetail: 'public-anamnese-diet-detail',
  mainGoal: 'public-anamnese-main-goal',
  goalDetails: 'public-anamnese-goal-details',
  takesMedication: 'public-anamnese-takes-medication',
  medications: 'public-anamnese-medications',
  hadSurgery: 'public-anamnese-had-surgery',
  surgeries: 'public-anamnese-surgeries',
  hasChronicDisease: 'public-anamnese-has-chronic-disease',
  chronicDiseases: 'public-anamnese-chronic-diseases',
  hasFamilyHistory: 'public-anamnese-has-family-history',
  familyHistoryDescription: 'public-anamnese-family-history',
  hasChildren: 'public-anamnese-has-children',
  timeSinceDelivery: 'public-anamnese-time-since-delivery',
  abdominalDiastasis: 'public-anamnese-abdominal-diastasis',
  diastasisTreatment: 'public-anamnese-diastasis-treatment',
  additionalInfo: 'public-anamnese-additional-info',
  referralSource: 'public-anamnese-referral-source',
  motivation: 'public-anamnese-motivation',
};

const FOCUSABLE_FIELD_SELECTOR = 'input:not([type="hidden"]), select, textarea, button';

const focusFieldContainer = (container: HTMLElement): void => {
  container.scrollIntoView({ behavior: 'smooth', block: 'center' });

  if (container.matches(FOCUSABLE_FIELD_SELECTOR)) {
    container.focus({ preventScroll: true });
    return;
  }

  const focusable = container.querySelector<HTMLElement>(FOCUSABLE_FIELD_SELECTOR);
  focusable?.focus({ preventScroll: true });
};

export const focusNearestPublicAnamneseError = (errors: Record<string, string>): void => {
  const firstErrorField = Object.keys(PUBLIC_ANAMNESE_FIELD_ELEMENT_IDS).find((field) => errors[field]);
  if (!firstErrorField) {
    return;
  }

  const elementId = PUBLIC_ANAMNESE_FIELD_ELEMENT_IDS[firstErrorField];
  const container = elementId ? document.getElementById(elementId) : null;
  if (!container) {
    return;
  }

  focusFieldContainer(container);
};

const buildFullGymName = (data: PublicAnamneseFormState): string => data.fullGymName.trim();

export type BuildPublicAnamnesePayloadOptions = {
  studentId?: number;
  studentToken?: string;
};

export const buildPublicAnamnesePayload = (
  data: PublicAnamneseFormState,
  options: BuildPublicAnamnesePayloadOptions = {},
): SubmitAnamnesisPayload => {
  const sanitized = sanitizePublicAnamneseForm(data);
  const cpfDigits = unmaskCpf(sanitized.cpf);

  return {
    studentId: options.studentId,
    studentToken: options.studentToken,
    studentName: sanitized.studentName.trim() || undefined,
    email: sanitized.email.trim() || undefined,
    cpf: cpfDigits.length === 11 ? cpfDigits : undefined,
    birthDate: sanitized.birthDate.trim() || undefined,
    heightCm: unmaskHeight(sanitized.heightCm).trim() || undefined,
    weightKg: unmaskWeight(sanitized.weightKg) || undefined,
    instagram: unmaskInstagram(sanitized.instagram) || undefined,
    profession: sanitized.profession.trim() || undefined,
    callingCode: sanitized.callingCode.trim() || undefined,
    phone: unmaskPhone(sanitized.phone) || undefined,
    cep: unmaskPostalCode(sanitized.cep, sanitized.country || 'BR') || undefined,
    street: sanitized.street.trim() || undefined,
    number: sanitized.number.trim() || undefined,
    complement: sanitized.complement.trim() || undefined,
    neighborhood: sanitized.neighborhood.trim() || undefined,
    city: sanitized.city.trim() || undefined,
    state: sanitized.state.trim() || undefined,
    country: sanitized.country.trim() || undefined,
    previousOnlineConsultancy: sanitized.previousOnlineConsultancy.trim() || undefined,
    courseFeedback: sanitized.courseFeedback.trim() || undefined,
    trainingLevelOther: sanitized.trainingLevel === 'other'
      ? sanitized.trainingLevelOther.trim() || undefined
      : undefined,
    gymEquipmentDescription: sanitized.gymEquipmentDescription.trim() || undefined,
    diastasisTreatment: sanitized.diastasisTreatment.trim() || undefined,
    onlineCourse: sanitized.onlineCourse || undefined,
    otherCourse: sanitized.otherCourse.trim() || undefined,
    trainingLevel: sanitized.trainingLevel || undefined,
    trainingLocation: sanitized.trainingLocation || undefined,
    availableEquipment: sanitized.availableEquipment.trim() || undefined,
    fullGymName: buildFullGymName(sanitized),
    weeklyAvailability: sanitized.weeklyAvailability.trim() || undefined,
    availableTrainingTime: sanitized.availableTrainingTime.trim() || undefined,
    hadInjury: sanitized.hadInjury,
    injuryDescription: sanitized.injuryDescription.trim() || undefined,
    hasPain: sanitized.hasPain,
    painLocation: sanitized.painLocation.trim() || undefined,
    painLimitsTraining: sanitized.painLimitsTraining,
    limitedExercises: sanitized.limitedExercises.trim() || undefined,
    currentlyExercises: sanitized.currentlyExercises,
    exerciseModalities: sanitized.exerciseModalities.trim() || undefined,
    timeWithoutExercise: sanitized.timeWithoutExercise.trim() || undefined,
    exerciseHistory: sanitized.exerciseHistory.trim() || undefined,
    enjoysTraining: sanitized.enjoysTraining,
    usedToEffort: sanitized.usedToEffort,
    hasNutritionalSupport: sanitized.hasNutritionalSupport,
    dietDetail: sanitized.dietDetail.trim() || undefined,
    takesMedication: sanitized.takesMedication,
    medications: sanitized.medications.trim() || undefined,
    hadSurgery: sanitized.hadSurgery,
    surgeries: sanitized.surgeries.trim() || undefined,
    hasChronicDisease: sanitized.hasChronicDisease,
    chronicDiseases: sanitized.chronicDiseases.trim() || undefined,
    hasFamilyHistory: sanitized.hasFamilyHistory,
    familyHistoryDescription: sanitized.familyHistoryDescription.trim() || undefined,
    hasChildren: sanitized.hasChildren,
    timeSinceDelivery: sanitized.timeSinceDelivery.trim() || undefined,
    abdominalDiastasis: sanitized.abdominalDiastasis,
    mainGoal: sanitized.mainGoal || undefined,
    goalDetails: sanitized.goalDetails.trim() || undefined,
    additionalInfo: sanitized.additionalInfo.trim() || undefined,
    referralSource: sanitized.referralSource || undefined,
    motivation: sanitized.motivation.trim() || undefined,
  };
};

export const maskPublicFieldValue = (
  field: keyof PublicAnamneseFormState,
  value: string,
  callingCode: string,
  country: string,
): string => {
  switch (field) {
    case 'cpf':
      return maskCpf(value);
    case 'phone':
      return maskPhoneByCountry(value, callingCode || '55');
    case 'cep':
      return maskPostalCode(value, country || 'BR');
    case 'heightCm':
      return maskHeight(value);
    case 'weightKg':
      return maskWeight(value);
    case 'instagram':
      return maskInstagram(value);
    default:
      return value;
  }
};

export const mapStudentDataToPublicAnamneseForm = (
  student: Student,
  phone: PhoneNumber | null,
  address: Address | null,
  anamnesis: Anamnesis | null,
): PublicAnamneseFormState => {
  const resolvedCode = resolveCallingCode(phone?.country_code || '55', phone?.country_iso ?? undefined);
  const country = address?.country ?? phone?.country_iso ?? 'BR';

  const base: PublicAnamneseFormState = {
    ...createInitialPublicAnamneseForm(),
    email: student.email || '',
    studentName: student.full_name || '',
    birthDate: student.birth_date || '',
    heightCm: student.height_m ? formatHeightMeters(student.height_m) : '',
    weightKg: student.weight_kg ? formatWeightKg(student.weight_kg) : '',
    cpf: student.cpf ? maskCpf(student.cpf) : '',
    cep: address?.zipcode ? maskPostalCode(address.zipcode, country) : '',
    street: address?.street || '',
    number: address?.street_number || '',
    complement: address?.complement || '',
    neighborhood: address?.neighborhood || '',
    city: address?.city || '',
    state: address?.state || '',
    country,
    callingCode: resolvedCode,
    phone: phone?.phone_number ? maskPhoneByCountry(phone.phone_number, resolvedCode) : '',
    instagram: student.instagram_username ? maskInstagram(student.instagram_username) : '',
  };

  if (!anamnesis) {
    return sanitizePublicAnamneseForm(base);
  }

  const injParts = (anamnesis.injury_description || '').split('\nDores: ');
  const injuryDesc = injParts[0] || '';
  const painLoc = injParts.length > 1 ? injParts[1] : '';

  return sanitizePublicAnamneseForm({
    ...base,
    onlineCourse: (anamnesis.online_course as PublicAnamneseFormState['onlineCourse']) || '',
    otherCourse: anamnesis.other_course || '',
    previousOnlineConsultancy: anamnesis.previous_online_consultancy || '',
    courseFeedback: anamnesis.course_feedback || '',
    trainingLevel: (anamnesis.training_level as PublicAnamneseFormState['trainingLevel']) || '',
    trainingLevelOther: anamnesis.training_level_other || '',
    trainingLocation: (anamnesis.training_location as PublicAnamneseFormState['trainingLocation']) || '',
    availableEquipment: anamnesis.available_equipment || '',
    fullGymName: anamnesis.full_gym_name || '',
    weeklyAvailability: anamnesis.weekly_availability || '',
    availableTrainingTime: anamnesis.available_training_time || '',
    hadInjury: anamnesis.had_injury ?? null,
    injuryDescription: injuryDesc,
    hasPain: anamnesis.has_pain ?? null,
    painLocation: anamnesis.pain_location || painLoc,
    painLimitsTraining: anamnesis.pain_limits_training ?? null,
    limitedExercises: anamnesis.limited_exercises || '',
    currentlyExercises: anamnesis.currently_exercises ?? null,
    exerciseModalities: anamnesis.exercise_modalities || '',
    timeWithoutExercise: anamnesis.time_without_exercise || '',
    exerciseHistory: anamnesis.exercise_history || '',
    enjoysTraining: anamnesis.enjoys_training ?? null,
    usedToEffort: anamnesis.used_to_effort ?? null,
    hasNutritionalSupport: anamnesis.has_nutritional_support ?? null,
    dietDetail: anamnesis.diet_detail || '',
    mainGoal: (anamnesis.main_goal as PublicAnamneseFormState['mainGoal']) || '',
    goalDetails: anamnesis.goal_details || '',
    takesMedication: anamnesis.takes_medication ?? null,
    medications: anamnesis.medications || '',
    hadSurgery: anamnesis.had_surgery ?? null,
    surgeries: anamnesis.surgeries || '',
    hasChronicDisease: anamnesis.has_chronic_disease ?? null,
    chronicDiseases: anamnesis.chronic_diseases || '',
    hasFamilyHistory: anamnesis.has_family_history ?? null,
    familyHistoryDescription: anamnesis.family_history_description || '',
    hasChildren: anamnesis.has_children ?? null,
    timeSinceDelivery: anamnesis.time_since_delivery || '',
    abdominalDiastasis: anamnesis.abdominal_diastasis ?? null,
    diastasisTreatment: anamnesis.diastasis_treatment || '',
    additionalInfo: anamnesis.additional_info || '',
    referralSource: (anamnesis.referral_source as PublicAnamneseFormState['referralSource']) || '',
    motivation: anamnesis.motivation || '',
    profession: anamnesis.profession || '',
  });
};
